import { Link, useNavigate } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { Alert, ApiError, AppLayout, Button, Card, Field, RequireSession } from 'vcode-commons-ui'
import BankIcon from '../components/BankIcon'
import { fieldErrors, useBanks, useCreateBankAccount, type Bank } from '../lib/banking'
import { compactIban, formatIban, ibanError } from '../lib/iban'

type BankChoice = { kind: 'existing'; bankId: string } | { kind: 'new'; name: string } | null

export default function NewAccountPage() {
  return (
    <RequireSession>
      {(user) => (
        <AppLayout user={user}>
          <Link
            to="/accounts"
            className="text-sm text-neutral-600 underline-offset-4 hover:underline dark:text-neutral-400"
          >
            ← Mes comptes
          </Link>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Ajouter un compte
          </h1>
          <NewAccountForm />
        </AppLayout>
      )}
    </RequireSession>
  )
}

export function NewAccountForm() {
  const navigate = useNavigate()
  const banks = useBanks()
  const create = useCreateBankAccount()

  const [choice, setChoice] = useState<BankChoice>(null)
  const [label, setLabel] = useState('')
  const [iban, setIban] = useState('')
  const [ibanTouched, setIbanTouched] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)

  const featured = banks.data?.filter((bank) => bank.isFeatured) ?? []
  const others = banks.data?.filter((bank) => !bank.isFeatured) ?? []
  const selectedId = choice?.kind === 'existing' ? choice.bankId : null
  const otherSelected = others.some((bank) => bank.bankId === selectedId)
  // Controle local des la sortie du champ ; le message du serveur, s'il y en
  // a un, l'emporte jusqu'a la prochaine frappe.
  const shownIbanError = errors.iban ?? (ibanTouched ? ibanError(iban) : null)

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    setFailure(null)

    const local: Record<string, string> = {}
    if (choice === null || (choice.kind === 'new' && choice.name.trim().length < 2)) {
      local.bank =
        choice?.kind === 'new'
          ? 'Le nom de la banque doit compter au moins deux caracteres.'
          : 'Choisissez la banque qui tient ce compte.'
    }
    if (label.trim().length === 0) {
      local.label = 'Le libelle du compte est obligatoire.'
    }
    const ibanProblem = ibanError(iban)
    if (ibanProblem !== null) {
      local.iban = ibanProblem
      setIbanTouched(true)
    }

    setErrors(local)
    if (Object.keys(local).length > 0 || choice === null) {
      return
    }

    create.mutate(
      {
        ...(choice.kind === 'existing' ? { bankId: choice.bankId } : { newBankName: choice.name.trim() }),
        label: label.trim(),
        iban: compactIban(iban),
      },
      {
        onSuccess: (account) =>
          void navigate({
            to: '/accounts',
            search: { created: account.label, ...(account.bank.status === 'pending' ? { pending: true } : {}) },
          }),
        onError: (error) => {
          const byField = error instanceof ApiError ? fieldErrors(error.payload) : {}
          setErrors(byField)
          setFailure(Object.keys(byField).length > 0 ? null : error.message)
        },
      },
    )
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mt-6 space-y-6">
      {failure !== null && <Alert>{failure}</Alert>}

      <Card title="Banque">
        {banks.isPending && <p className="text-sm text-neutral-500">Chargement des banques...</p>}
        {banks.isError && <Alert>{banks.error.message}</Alert>}

        {banks.isSuccess && (
          <div className="space-y-5">
            <div role="radiogroup" aria-label="Banques principales" className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {featured.map((bank) => (
                <BankTile
                  key={bank.bankId}
                  bank={bank}
                  selected={bank.bankId === selectedId}
                  onSelect={() => setChoice({ kind: 'existing', bankId: bank.bankId })}
                />
              ))}
            </div>

            <label className="block">
              <span className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
                Autre banque
              </span>
              <select
                value={otherSelected ? (selectedId ?? '') : ''}
                onChange={(event) =>
                  setChoice(event.target.value === '' ? null : { kind: 'existing', bankId: event.target.value })
                }
                className="mt-1.5 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-100"
              >
                <option value="">Choisir dans la liste...</option>
                {others.map((bank) => (
                  <option key={bank.bankId} value={bank.bankId}>
                    {bank.name}
                  </option>
                ))}
              </select>
            </label>

            {choice?.kind === 'new' ? (
              <div className="rounded-xl border border-dashed border-neutral-300 p-4 dark:border-neutral-700">
                <Field
                  label="Nom de la banque a ajouter"
                  value={choice.name}
                  maxLength={80}
                  autoFocus
                  onChange={(event) => setChoice({ kind: 'new', name: event.target.value })}
                  hint="Votre compte est cree tout de suite. La banque n'apparaitra dans la liste qu'apres validation par un administrateur."
                />
                <button
                  type="button"
                  onClick={() => setChoice(null)}
                  className="mt-2 text-sm text-neutral-600 underline underline-offset-4 dark:text-neutral-400"
                >
                  Annuler
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setChoice({ kind: 'new', name: '' })}
                className="text-sm font-medium text-neutral-900 underline underline-offset-4 dark:text-neutral-100"
              >
                + Ma banque n'est pas dans la liste
              </button>
            )}

            {errors.bank !== undefined && <FieldError>{errors.bank}</FieldError>}
          </div>
        )}
      </Card>

      <Card title="Compte">
        <div className="space-y-4">
          <div>
            <Field
              label="Libelle"
              value={label}
              maxLength={80}
              placeholder="Compte courant, Livret A, Compte joint..."
              onChange={(event) => setLabel(event.target.value)}
            />
            {errors.label !== undefined && <FieldError>{errors.label}</FieldError>}
          </div>

          <div>
            <Field
              label="IBAN"
              value={formatIban(iban)}
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="FR76 3000 6000 0112 3456 7890 189"
              onChange={(event) => {
                setIban(compactIban(event.target.value))
                setErrors(({ iban: _stale, ...rest }) => rest)
              }}
              onBlur={() => setIbanTouched(iban.length > 0)}
              hint="Il est chiffre avant d'etre enregistre ; seuls les quatre derniers caracteres restent lisibles."
            />
            {shownIbanError !== null && <FieldError>{shownIbanError}</FieldError>}
          </div>
        </div>
      </Card>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Link
          to="/accounts"
          className="rounded-lg px-4 py-2.5 text-center text-sm font-medium text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          Annuler
        </Link>
        <div className="sm:w-48">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creation...' : 'Creer le compte'}
          </Button>
        </div>
      </div>
    </form>
  )
}

function BankTile({ bank, selected, onSelect }: { bank: Bank; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition ${
        selected
          ? 'border-neutral-900 bg-neutral-900/5 ring-2 ring-neutral-900/15 dark:border-neutral-100 dark:bg-neutral-100/10 dark:ring-neutral-100/20'
          : 'border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:border-neutral-600 dark:hover:bg-neutral-800/50'
      } text-neutral-900 dark:text-neutral-100`}
    >
      <BankIcon bank={bank} size="sm" />
      <span className="min-w-0 truncate">{bank.name}</span>
    </button>
  )
}

function FieldError({ children }: { children: string }) {
  return <p className="mt-1.5 text-sm text-red-700 dark:text-red-300">{children}</p>
}
